import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { updateCredits } from '../slices/authSlice';
import { 
    StarIcon, 
    RocketLaunchIcon,
    DocumentTextIcon,
    CreditCardIcon,
    GiftIcon,
    TrashIcon,
    ShieldCheckIcon
} from '@heroicons/react/24/outline';
import useApiWithAuth from '../utils/useApiWithAuth';
import authFetch from '../utils/authFetch';
import ModalDeleteAccount from './ModalDeleteAccount';

const UserDashboard = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [userStats, setUserStats] = useState({});
    const [referralStats, setReferralStats] = useState(null);
    const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
    const { apiCall } = useApiWithAuth();
    const dispatch = useDispatch();

    const token = useSelector((state) => state.auth.token);
    const userId = useSelector((state) => state.auth.user.id);
    const user = useSelector((state) => state.auth.user);

    const fetchDashboardData = useCallback(async () => {
        try {
            // Récupérer l'historique des analyses
            const response = await authFetch(`https://cvanalyzerback.osc-fr1.scalingo.io/api/cv/history/${userId}`);

            if (response.ok) {
                const data = await response.json();
                calculateStats(data.analyses || []);
            }

            // Récupérer les stats de parrainage
            try {
                const referralResponse = await apiCall('https://cvanalyzerback.osc-fr1.scalingo.io/api/referrals/stats');
                const referralData = await referralResponse.json();
                setReferralStats(referralData);
            } catch (error) {
                // Ignorer les erreurs de parrainage
            }

            // Rafraîchir les crédits de l'utilisateur
            try {
                const creditsResponse = await authFetch(`https://cvanalyzerback.osc-fr1.scalingo.io/api/users/${userId}/credits`);
                if (creditsResponse.ok) {
                    const creditsData = await creditsResponse.json();
                    if (creditsData.credits !== undefined) {
                        dispatch(updateCredits(parseFloat(creditsData.credits)));
                        console.log('✅ Crédits mis à jour dans le dashboard:', creditsData.credits);
                    }
                }
            } catch (error) {
                // Ignorer les erreurs de récupération des crédits
                console.error('Erreur lors du rafraîchissement des crédits:', error);
            }
        } catch (error) {
            // Erreur silencieuse pour le chargement du dashboard
        } finally {
            setIsLoading(false);
        }
    }, [token, userId, apiCall, dispatch]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    const calculateStats = (analysesData) => {
        const totalAnalyses = analysesData.length;
        const averageScore = totalAnalyses > 0 
            ? Math.round(analysesData.reduce((acc, a) => acc + (a.score || 0), 0) / totalAnalyses)
            : 0;
        const bestScore = Math.max(...analysesData.map(a => a.score || 0), 0);
        const sectorsTested = new Set(analysesData.map(a => a.sector)).size;
        const thisMonth = analysesData.filter(a => 
            new Date(a.createdAt) >= new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        ).length;

        // Calculer la progression
        // L'API retourne les analyses en DESC (plus récent en premier)
        // On inverse pour avoir l'ordre chronologique (plus ancien → plus récent)
        const scoresChronological = [...analysesData]
            .reverse()
            .map(a => a.score || 0)
            .filter(s => s > 0);
        const recentScores = scoresChronological.slice(-10);
        
        let progression = 0;
        if (recentScores.length >= 2) {
            const firstHalf = recentScores.slice(0, Math.floor(recentScores.length / 2));
            const secondHalf = recentScores.slice(Math.floor(recentScores.length / 2));
            const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
            const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
            progression = Math.round(((secondAvg - firstAvg) / firstAvg) * 100);
        }

        setUserStats({
            totalAnalyses,
            averageScore,
            bestScore,
            sectorsTested,
            thisMonth,
            progression
        });
    };

    if (isLoading) {
        return (
            <div className="page-shell flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Chargement du dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-shell">
            <div className="page-wrap">
                <div className="page-header">
                    <div>
                        <h1 className="page-title">Mon compte</h1>
                        <p className="page-subtitle">{user.username}</p>
                    </div>
                    <Link to="/upload" className="btn-primary shrink-0">
                        Analyser un CV
                    </Link>
                </div>

                <div className="card-surface p-5 sm:p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">Vos crédits</h2>
                    <div className="text-3xl font-semibold text-gray-900 mb-1">
                        {user.credits || 0}
                    </div>
                    <p className="text-gray-500 text-sm mb-4">crédits disponibles</p>
                    <Link to="/subscription" className="btn-secondary">
                        {user?.hasPaid ? 'Recharger' : 'Débloquer le rapport'}
                    </Link>
                </div>

                {/* Welcome card — uniquement pour les nouveaux utilisateurs */}
                {userStats.totalAnalyses === 0 && (
                    <div className="bg-blue-50 rounded-xl p-6 mb-8 border border-blue-100">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900 mb-1">Bienvenue, {user.username}</h2>
                                <p className="text-gray-600 text-sm mb-2">
                                    Solde :{' '}
                                    <strong>
                                        {(Number.isFinite(Number(user?.credits))
                                            ? Number(user.credits)
                                            : 0
                                        ).toFixed(2)}{' '}
                                        crédits
                                    </strong>
                                    {' '}— score et 3 conseils offerts. Rapport complet dès 5 €.
                                </p>
                                <p className="text-gray-500 text-xs">
                                    Conseil : collez une offre d&apos;emploi pour un score de compatibilité ciblé.
                                </p>
                            </div>
                            <Link
                                to="/upload"
                                className="btn-primary flex-shrink-0"
                            >
                                Analyser mon CV
                            </Link>
                        </div>
                    </div>
                )}

                {/* Stats — 4 cartes */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <div className="card-surface p-4">
                        <div className="flex items-center justify-between mb-1">
                            <p className="text-xs font-medium text-gray-500">Analyses</p>
                            <DocumentTextIcon className="w-5 h-5 text-blue-400" />
                        </div>
                        <p className="text-3xl font-bold text-blue-600">{userStats.totalAnalyses}</p>
                    </div>

                    <div className="card-surface p-4">
                        <div className="flex items-center justify-between mb-1">
                            <p className="text-xs font-medium text-gray-500">Score moyen</p>
                            <StarIcon className="w-5 h-5 text-green-400" />
                        </div>
                        <p className="text-3xl font-bold text-green-600">{userStats.averageScore > 0 ? `${userStats.averageScore}/100` : '—'}</p>
                    </div>

                    <div className="card-surface p-4">
                        <div className="flex items-center justify-between mb-1">
                            <p className="text-xs font-medium text-gray-500">Meilleur score</p>
                            <StarIcon className="w-5 h-5 text-yellow-400" />
                        </div>
                        <p className="text-3xl font-bold text-yellow-500">{userStats.bestScore > 0 ? `${userStats.bestScore}/100` : '—'}</p>
                    </div>

                    <div className="card-surface p-4">
                        <div className="flex items-center justify-between mb-1">
                            <p className="text-xs font-medium text-gray-500">Progression</p>
                            <RocketLaunchIcon className="w-5 h-5 text-purple-400" />
                        </div>
                        <p className={`text-3xl font-bold ${userStats.progression > 0 ? 'text-green-600' : userStats.progression < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                            {userStats.progression > 0 ? `+${userStats.progression}%` : userStats.progression < 0 ? `${userStats.progression}%` : userStats.totalAnalyses < 2 ? '—' : '='}
                        </p>
                    </div>
                </div>

                {/* Badge Ambassadeur */}
                {referralStats && referralStats.completedReferrals >= 5 && (
                    <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl shadow-xl p-6 mb-8 text-white">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                                    <GiftIcon className="w-8 h-8" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold mb-1">🏆 Badge Ambassadeur</h3>
                                    <p className="text-yellow-100">
                                        Félicitations ! Vous avez parrainé {referralStats.completedReferrals} personnes avec succès
                                    </p>
                                </div>
                            </div>
                            <Link
                                to="/referral"
                                className="px-6 py-3 bg-white text-orange-600 font-semibold rounded-xl hover:bg-yellow-50 transition-all"
                            >
                                Voir mes stats
                            </Link>
                        </div>
                    </div>
                )}

                {/* Actions rapides */}
                <div className="card-surface p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Parrainage</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Link
                            to="/referral"
                            className="flex items-center p-4 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50"
                        >
                            <GiftIcon className="w-5 h-5 mr-3 text-gray-400 flex-shrink-0" />
                            <div>
                                <p className="font-medium text-sm">Parrainer</p>
                                <p className="text-xs text-gray-500">
                                    {referralStats ? `${referralStats.totalCreditsEarned} crédits gagnés` : 'Gagnez des crédits'}
                                </p>
                            </div>
                        </Link>
                    </div>
                </div>

                {/* Section RGPD - Suppression du compte */}
                <div className="mt-6 card-surface p-6">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <ShieldCheckIcon className="w-6 h-6 text-gray-600" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-lg font-bold text-gray-800 mb-1">
                                Données personnelles et RGPD
                            </h2>
                            <p className="text-sm text-gray-600 mb-4">
                                Conformément au RGPD, vous pouvez demander la suppression définitive de votre compte et de toutes les données associées (droit à l&apos;effacement, article 17).
                            </p>
                            <button
                                type="button"
                                onClick={() => setShowDeleteAccountModal(true)}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl transition-colors border border-red-200 hover:border-red-300"
                            >
                                <TrashIcon className="w-5 h-5" />
                                Supprimer mon compte
                            </button>
                        </div>
                    </div>
                </div>

            </div>

            {showDeleteAccountModal && (
                <ModalDeleteAccount onClose={() => setShowDeleteAccountModal(false)} />
            )}
        </div>
    );
};

export default UserDashboard; 